import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../../Environments/environment";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import { apiClient } from "../../../api";
import { BUSINESS_TYPES } from "../../../utils/businessTypes";

const UpdateProperty = () => {
  const [propertyData, setPropertyData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    website: "",
    about: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zipcode: "",
    countryCode: "",
    logo: null,
    cover: null,
    standardCheckinTime: "",
    standardCheckoutTime: "",
    timezone: "",
    defaultNewDayTime: "",
    businessType: "hotel",
  });
  const { selectedPropertyId } = useProperty();
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedPropertyId) {
      fetchPropertyData(selectedPropertyId);
    }
  }, [selectedPropertyId]);

  const fetchPropertyData = async (propertyId) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/property/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        const { property, guestPropertyMobile } = response.data.result.property;
        const { setting } = response.data.result;

        setPropertyData({
          name: property.name || "",
          email: property.email || "",
          phoneNumber: property.phoneNumber || "",
          website: property.website || "",
          about: property.about || "",
          country: property.country || "",
          state: property.state || "",
          city: property.city || "",
          address: property.address || "",
          zipcode: property.zipcode || "",
          countryCode: property.countryCode || "",
          logo: property.logoUrl || null,
          cover: property.coverUrl || null,
          guestPropertyMobile: guestPropertyMobile.phoneNumber || "",
          guestPropertyCountryCode: guestPropertyMobile.countryCode || "",
          standardCheckinTime: setting.standardCheckinTime || "",
          standardCheckoutTime: setting.standardCheckoutTime || "",
          timezone: setting.timezone || "",
          defaultNewDayTime: setting.defaultNewDayTime || "",
          businessType: property.businessType || "hotel",
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error fetching property data.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Create a FormData object to handle file uploads
      const formData = new FormData();

      // Append the propertyData fields to the formData
      for (const key in propertyData) {
        if (key !== "logo" && key !== "cover") {
          if (key === "phoneNumber") {
            setPropertyData({
              ...propertyData,
              phoneNumber: normalizePhone(propertyData[key]),
            });

            formData.append(key, normalizePhone(propertyData[key]));
          } else {
            formData.append(key, propertyData[key]);
          }
        }
      }

      // Append the logo and cover files to the formData
      if (propertyData.logo instanceof File) {
        formData.append("logo", propertyData.logo);
      }
      if (propertyData.cover instanceof File) {
        formData.append("cover", propertyData.cover);
      }

      // Send the PUT request with formData
      const response = await apiClient.put(
        `${baseUrl}/property/${selectedPropertyId}`,
        formData, // Send the FormData object
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Property updated successfully.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error updating property.";
      toast.error(errorMessage);
    }
  };
  function normalizePhone(input) {
    if (!input) return "";
    return String(input).replace(/[^\d]/g, "");
  }
  return (
    <div className="relative bg-white rounded-lg shadow p-4 sm:p-6 w-full max-w-7xl mx-auto h-auto">
      {/* Modal Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          Listing Profile
        </h2>
        <button
          className="bg-green-400 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-sm sm:text-base font-medium hover:bg-green-500 w-full sm:w-auto"
          onClick={handleSaveChanges}
        >
          Save Changes
        </button>
      </div>

      {/* Property Form */}
      <div className="mt-4 sm:mt-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Skeleton for Property Name */}
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Property Email */}
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Phone Number */}
            <div>
              <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="flex gap-0">
                <div className="h-10 w-20 bg-gray-200 rounded-l animate-pulse"></div>
                <div className="h-10 flex-1 bg-gray-200 rounded-r animate-pulse"></div>
              </div>
            </div>

            {/* Skeleton for Website */}
            <div>
              <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for About */}
            <div className="col-span-1 sm:col-span-2">
              <div className="h-4 w-36 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-24 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Logo */}
            <div>
              <div className="h-4 w-16 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-300 px-4 sm:px-6 py-6 sm:py-10">
                <div className="text-center w-full">
                  <div className="h-20 w-20 sm:h-24 sm:w-24 bg-gray-200 rounded mx-auto animate-pulse"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded mx-auto mt-4 animate-pulse"></div>
                  <div className="h-3 w-32 bg-gray-200 rounded mx-auto mt-2 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Skeleton for Cover Photo */}
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-300 px-4 sm:px-6 py-6 sm:py-10">
                <div className="text-center w-full">
                  <div className="h-24 w-24 sm:h-32 sm:w-32 bg-gray-200 rounded mx-auto animate-pulse"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded mx-auto mt-4 animate-pulse"></div>
                  <div className="h-3 w-32 bg-gray-200 rounded mx-auto mt-2 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Skeleton for Country */}
            <div>
              <div className="h-4 w-20 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Zip/Postal Code */}
            <div>
              <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for State */}
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for City */}
            <div>
              <div className="h-4 w-16 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Address */}
            <div className="col-span-1 sm:col-span-2">
              <div className="h-4 w-36 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Skeleton for Business Type */}
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Empty div for spacing */}
            <div></div>
          </div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Property Name */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Listing Name
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter Listing name"
                value={propertyData.name}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, name: e.target.value })
                }
              />
            </div>

            {/* Property Email */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Listing Email
              </label>
              <input
                type="email"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter Listing email"
                value={propertyData.email}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, email: e.target.value })
                }
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Phone Number
              </label>
              <div className="flex mt-1">
                <select
                  value={propertyData.countryCode}
                  onChange={(e) =>
                    setPropertyData({
                      ...propertyData,
                      countryCode: e.target.value,
                    })
                  }
                  className="rounded-l-md border-r-0 border-gray-300 py-2 pl-3 pr-6 sm:pr-8 focus:border-indigo-500 focus:ring-indigo-500 text-sm sm:text-base min-w-[80px] flex-shrink-0"
                >
                  <option value="+1">+1</option>
                  <option value="+91">+91</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  Add more country codes as needed
                </select>
                <input
                  type="tel"
                  className="block flex-1 rounded-r-md border-l-0 border-gray-300 px-3 sm:px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 text-sm sm:text-base"
                  placeholder="Enter phone number"
                  value={propertyData.phoneNumber}
                  onChange={(e) =>
                    setPropertyData({
                      ...propertyData,
                      phoneNumber: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Property Website */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Listing Website
              </label>
              <input
                type="url"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter Listing website"
                value={propertyData.website}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    website: e.target.value,
                  })
                }
              />
            </div>

            {/* About Property */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                About the Listing
              </label>
              <textarea
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                rows="3"
                placeholder="Write something about the Listing"
                value={propertyData.about}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, about: e.target.value })
                }
              />
            </div>

            {/* Logo photo */}
            <div className="col-span-1 sm:col-span-1">
              <label
                htmlFor="Logo"
                className="block text-xs sm:text-sm font-medium mb-2 text-gray-700"
              >
                Logo
              </label>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 sm:px-6 py-6 sm:py-10">
                <div className="text-center w-full">
                  {propertyData.logo ? (
                    <img
                      src={
                        propertyData.logo instanceof File
                          ? URL.createObjectURL(propertyData.logo)
                          : propertyData.logo
                      }
                      alt="Logo"
                      className="h-20 w-20 sm:h-24 sm:w-24 object-cover mx-auto max-w-full"
                    />
                  ) : (
                    <PhotoIcon
                      aria-hidden="true"
                      className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-gray-300"
                    />
                  )}
                  {/* Upload input */}
                  <label
                    htmlFor="logo-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500 text-xs sm:text-sm mt-3 sm:mt-4"
                  >
                    <span className="text-right">Upload a file</span>
                    <input
                      id="logo-upload"
                      name="logo-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (e.target.files[0].type === "image/svg+xml") {
                          alert("SVG files are not supported");
                          return;
                        }
                        if (e.target.files[0].size > 10 * 1024 * 1024) {
                          alert("Please upload a file smaller than 10MB");
                          return;
                        }
                        setPropertyData({
                          ...propertyData,
                          logo: file,
                        });
                      }}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-xs leading-5 text-gray-600 mt-2">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>
            </div>

            {/* Cover photo */}
            <div className="col-span-1 sm:col-span-1">
              <label
                htmlFor="cover-photo"
                className="block text-xs sm:text-sm font-medium mb-2 text-gray-700"
              >
                Cover photo
              </label>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 sm:px-6 py-6 sm:py-10">
                <div className="text-center w-full">
                  {propertyData.cover ? (
                    <img
                      src={
                        propertyData.cover instanceof File
                          ? URL.createObjectURL(propertyData.cover)
                          : propertyData.cover
                      }
                      alt="Cover"
                      className="h-24 w-24 sm:h-32 sm:w-32 object-cover mx-auto max-w-full"
                    />
                  ) : (
                    <PhotoIcon
                      aria-hidden="true"
                      className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-gray-300"
                    />
                  )}
                  {/* Upload input */}
                  <label
                    htmlFor="cover-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500 text-xs sm:text-sm mt-3 sm:mt-4"
                  >
                    <span className="text-right">Upload a file</span>
                    <input
                      id="cover-upload"
                      name="cover-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (e.target.files[0].type === "image/svg+xml") {
                          alert("SVG files are not supported");
                          return;
                        }
                        if (e.target.files[0].size > 10 * 1024 * 1024) {
                          alert("Please upload a file smaller than 10MB");
                          return;
                        }
                        setPropertyData({
                          ...propertyData,
                          cover: file, // Store the file object
                        });
                      }}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-xs leading-5 text-gray-600 mt-2">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Country
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter country"
                value={propertyData.country}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    country: e.target.value,
                  })
                }
              />
            </div>

            {/* Zip/Postal Code */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Zip/Postal Code
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter zip or postal code"
                value={propertyData.zipcode}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    zipcode: e.target.value,
                  })
                }
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                State/Province
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter state or province"
                value={propertyData.state}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, state: e.target.value })
                }
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                City
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter city"
                value={propertyData.city}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, city: e.target.value })
                }
              />
            </div>

            {/* Property Address */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Listing Address
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-sm sm:text-base"
                placeholder="Enter Listing address"
                value={propertyData.address}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    address: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                Business Type
              </label>

              <select
                value={propertyData.businessType}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    businessType: e.target.value,
                  })
                }
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 text-black text-xs sm:text-sm mt-1"
              >
                <option value="">Select a business type</option>
                {BUSINESS_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div></div>
            {/*      
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Standard Check-in Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter standard check-in time"
                value={propertyData.standardCheckinTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    standardCheckinTime: e.target.value,
                  })
                }
              />
            </div>

        
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Standard Check-out Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter standard check-out time"
                value={propertyData.standardCheckoutTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    standardCheckoutTime: e.target.value,
                  })
                }
              />
            </div>

          
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Timezone
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter timezone"
                value={propertyData.timezone}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    timezone: e.target.value,
                  })
                }
              />
            </div>

    
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Default New Day Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter default new day time"
                value={propertyData.defaultNewDayTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    defaultNewDayTime: e.target.value,
                  })
                }
              />
            </div> */}
          </form>
        )}
      </div>
    </div>
  );
};

export default UpdateProperty;
